import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { RunningRouteVector, RouteCoordinate } from './RunningRouteVector';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

// Exact format for share image - no rounding, 1 decimal only when fractional
const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};
// Pace: always whole seconds (0:37 not 0:37.5)
const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Add proper spacing to workout names: "ChestDay" -> "Chest Day", "UPPER_BODY" -> "Upper Body"
const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

export const ShareTemplateClassic = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const formatTime = (minutes: number) => {
      const h = Math.floor(minutes / 60);
      const m = Math.floor(minutes % 60);
      const s = Math.round((minutes % 1) * 60);
      if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return `${m}:${String(s).padStart(2, '0')}`;
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    };

    const isRunning = workout.workout_type?.toLowerCase() === 'running';
    const routeCoordinates: RouteCoordinate[] = (workout as any).route_coordinates || [];

    return (
      <div
        ref={ref}
        className="flex flex-col items-center w-full"
        style={{
          width: '1080px',
          minWidth: '1080px',
          height: '1980px',
          background: 'linear-gradient(180deg, #0a0f0d 0%, #0d1512 50%, #0a1210 100%)',
          border: '3px solid rgba(10, 255, 194, 0.2)',
          borderRadius: '48px',
          padding: '80px',
          boxSizing: 'border-box',
        }}
      >
        {/* Date in corner */}
        <div className="flex justify-end mb-8">
          <span className="font-medium" style={{ fontSize: '32px', color: 'rgba(255,255,255,0.7)' }}>
            {formatDate(workout.completed_at)}
          </span>
        </div>

        {/* Centered Logo */}
        <div className="flex justify-center mb-6">
          <img src={straivLogo} alt="STRAIV" style={{ width: '400px', height: 'auto' }} />
        </div>

        {/* Title */}
        <h2 className="font-bold text-center mb-4" style={{ fontSize: '56px', color: '#0AFFC2' }}>
          {formatShareTitle(workout.title)}
        </h2>
        <p className="text-center mb-12" style={{ fontSize: '36px', color: 'rgba(255,255,255,0.5)' }}>
          {t('components.shareTemplatesExtra.workoutComplete')}
        </p>

        {/* Stats Card - gym only */}
        {!isRunning && (
          <div 
            className="mb-12"
            style={{
              background: 'linear-gradient(135deg, rgba(10, 80, 60, 0.4) 0%, rgba(10, 60, 50, 0.3) 100%)',
              border: '2px solid rgba(10, 255, 194, 0.15)',
              borderRadius: '32px',
              padding: '48px',
            }}
          >
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <p className="font-bold" style={{ fontSize: '72px', color: '#0AFFC2' }}>
                  {workout.total_sets || 0}
                </p>
                <p style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplatesExtra.setsLower')}</p>
              </div>
              <div>
                <p className="font-bold" style={{ fontSize: '72px', color: '#0AFFC2' }}>
                  {workout.total_volume ? Math.round(workout.total_volume) : 0}
                </p>
                <p style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplatesExtra.kgVolumeLower')}</p>
              </div>
              <div>
                <p className="font-bold" style={{ fontSize: '72px', color: '#0AFFC2' }}>
                  {formatTime(workout.duration_minutes)}
                </p>
                <p style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplatesExtra.timeLower')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Running route + stats (or stats only when no route) */}
        {isRunning && (
          <div className="flex flex-col items-center mb-12">
            {routeCoordinates.length >= 2 && (
              <RunningRouteVector
                coordinates={routeCoordinates}
                width={700}
                height={500}
                strokeColor="#0AFFC2"
                strokeWidth={3}
                showStartEnd={true}
              />
            )}
            <div className="grid grid-cols-4 gap-6 text-center w-full mt-12" style={{ padding: '0 16px' }}>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplates.distance')}</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>{workout.distance_km || '0'}</p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplates.pace')}</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>
                  {(workout as any).pace_seconds_per_km != null ? formatPaceExact((workout as any).pace_seconds_per_km) : (workout.average_pace || '--:--')}
                </p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplates.time')}</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>
                  {(workout as any).duration_seconds != null ? formatTimeExact((workout as any).duration_seconds) : formatTime(workout.duration_minutes)}
                </p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>{t('components.shareTemplates.calories')}</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>{workout.calories_burned || '--'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Exercises List (gym only) */}
        {!isRunning && workout.exercises && workout.exercises.length > 0 && (
          <div className="flex-1 w-full max-w-full flex flex-col items-center">
            <div className="space-y-4 w-full max-w-full">
              {workout.exercises.slice(0, 5).map((exercise, index) => (
                <div 
                  key={index}
                  className="flex justify-between items-center"
                  style={{ 
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '16px',
                    padding: '24px 32px',
                  }}
                >
                  <span className="text-white" style={{ fontSize: '36px' }}>{formatShareTitle(exercise.name)}</span>
                  <span style={{ fontSize: '32px', color: 'rgba(255,255,255,0.5)' }}>
                    {exercise.sets}×{exercise.reps || '-'}
                    {exercise.weight_kg ? ` @ ${exercise.weight_kg}kg` : ''}
                  </span>
                </div>
              ))}
              {workout.exercises.length > 5 && (
                <p className="text-center pt-4" style={{ fontSize: '28px', color: 'rgba(255,255,255,0.4)' }}>
                  {t('components.shareTemplatesExtra.moreSuffix', { count: workout.exercises.length - 5 })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-12 mt-auto" style={{ borderTop: '2px solid rgba(255,255,255,0.1)' }}>
          <p style={{ fontSize: '28px', color: 'rgba(255,255,255,0.4)' }}>
            {t('components.shareTemplatesExtra.poweredBy')}
          </p>
        </div>
      </div>
    );
  }
);

ShareTemplateClassic.displayName = 'ShareTemplateClassic';
